import React, { useState, useEffect } from 'react';
import { DownloadJobsAPI } from '../../api/client';
import Loading from '../../components/Common/Loading';

const DownloadsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadJobs();

    // Auto-refresh every 5 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadJobs(true); // Silent refresh
      }, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const loadJobs = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const data = await DownloadJobsAPI.list({ ordering: '-created_at' });
      // Handle both paginated and non-paginated responses
      const jobsArray = Array.isArray(data) ? data : (data.results || []);
      setJobs(jobsArray);
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.detail || 'Failed to load download jobs');
        console.error('Error loading jobs:', err);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'yellow', label: 'Pending' },
      started: { color: 'blue', label: 'In Progress' },
      ready: { color: 'green', label: 'Ready' },
      failed: { color: 'red', label: 'Failed' },
    };

    const config = statusConfig[status] || { color: 'gray', label: status };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${config.color}-100 text-${config.color}-800`}>
        {config.label}
      </span>
    );
  };

  const handleDownload = (job) => {
    if (job.download_url) {
      window.open(job.download_url, '_blank');
    }
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;

    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading message="Loading download jobs..." size="page" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading download jobs</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Downloads</h1>
            <p className="mt-2 text-gray-600">
              Monitor and access your download jobs
            </p>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Auto-refresh
            </label>
            <button
              onClick={() => loadJobs()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No download jobs</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create a download job from the Studies page to see it here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Job Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Job #{job.id}
                    </h3>
                    {getStatusBadge(job.status)}
                  </div>

                  {/* Job Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Created:</span>{' '}
                      {new Date(job.created_at).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">D-Folders:</span>{' '}
                      {job.folder_ids?.length || 0}
                    </div>
                    {job.updated_at && job.updated_at !== job.created_at && (
                      <div>
                        <span className="font-medium">Updated:</span>{' '}
                        {new Date(job.updated_at).toLocaleString()}
                      </div>
                    )}
                    {job.download_url_expires_at && (
                      <div>
                        <span className="font-medium">Link expires:</span>{' '}
                        <span className={getTimeRemaining(job.download_url_expires_at) === 'Expired' ? 'text-red-600' : ''}>
                          {getTimeRemaining(job.download_url_expires_at)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Error Message */}
                  {job.status === 'failed' && job.error_message && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">
                        <span className="font-medium">Error:</span> {job.error_message}
                      </p>
                    </div>
                  )}

                  {/* Progress Indicator */}
                  {job.status === 'started' && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 rounded-full animate-pulse" style={{ width: '60%' }} />
                        </div>
                        <span className="text-xs text-gray-500">Processing...</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Button */}
                {job.status === 'READY' && job.download_url && (
                  <button
                    onClick={() => handleDownload(job)}
                    className="ml-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                    Download
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {jobs.length > 0 && (
        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">{jobs.length}</div>
              <div className="text-sm text-gray-600">Total Jobs</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {jobs.filter(j => j.status === 'started').length}
              </div>
              <div className="text-sm text-gray-600">In Progress</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {jobs.filter(j => j.status === 'ready').length}
              </div>
              <div className="text-sm text-gray-600">Ready</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {jobs.filter(j => j.status === 'failed').length}
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadsPage;
